import React from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ViewerProvider, actions } from '@store/viewerModule/base';
import usePageTitle from '@src/hooks/usePageTitle';
import { sharingResponseSelector } from '@store/viewerModule/sharing/selectors';
import { dashboardResponseSelector } from '@store/viewerModule/dashboard/selectors';
import { allTrafficFilterSelector } from '@store/filterContainer/selectors';
import {
  persistenceResponseSelector,
  persistenceKeySelector,
} from '@store/persistentConfig/selectors';
import { PAGE_TYPE } from '@utils/constants';
import Content from './Content';
import TopPanel from './TopPanel';
import { Wrapper } from './styled';
import VideoSummarize from '@src/components/viewer-module/AISummary/VideoSummarizeContainer';
import { getMetricTable } from '@src/store/viewerModule/base/helpers';

const ViewerModulePage = (props) => {
  const { pageType } = props;
  const reduxDispatch = useDispatch();
  const { viewerId } = useParams<{ viewerId: string }>();
  const { id = `vm-${viewerId}` } = useParams<{ id?: string }>();
  const { fid } = useSelector(allTrafficFilterSelector) || { fid: null };
  const response = useSelector(sharingResponseSelector);
  const dashboardResponse = useSelector(dashboardResponseSelector);
  const persistenceData = useSelector(persistenceResponseSelector);
  const persistenceKey = useSelector(persistenceKeySelector);

  usePageTitle({ text: viewerId ? `Viewer - ${viewerId}` : 'Viewers' });

  const vmPersistenceData = persistenceKey
    ? persistenceData?.[persistenceKey]?.response?.configuration?.vm
    : undefined;

  const initValues = (() => {
    const base: Record<string, any> = {
      id,
      viewerId,
      filterId: fid,
      pageType,
    };

    // https://conviva.atlassian.net/browse/PUI-1584
    // Set default columns from persistence data due to PUI-1584.
    // It seems like is's a problem because of the inconsistency of the redux and ViewerProvider context.
    if (vmPersistenceData?.selectedColumns) {
      base.selectedColumns = getMetricTable(vmPersistenceData.selectedColumns);
    }

    const configData = {
      [PAGE_TYPE.SHARING]: response?.state?.vmStore,
      [PAGE_TYPE.SAVED_DASHBOARD]: dashboardResponse?.config?.vmStore,
    }[pageType];

    if (configData) {
      base.viewerId = configData.viewerId;
      base.interval = configData.interval;

      if (Array.isArray(configData.selectedColumns)) {
        base.selectedColumns = getMetricTable(configData.selectedColumns);
      }
    }

    return base;
  })();

  // Update redux store with selected columns, only for saved dashboard and sharing page
  React.useEffect(() => {
    if (
      [PAGE_TYPE.SAVED_DASHBOARD, PAGE_TYPE.SHARING].includes(pageType) &&
      initValues.selectedColumns?.length
    ) {
      reduxDispatch(
        actions.setSelectedColumns({
          selectedColumns: initValues.selectedColumns,
          notUpdatePersistence: true,
        }),
      );
    }
  }, [reduxDispatch, initValues.selectedColumns, pageType]);

  return (
    <React.Fragment key={`${viewerId}-${id}`}>
      <ViewerProvider initValues={initValues}>
        <Wrapper>
          <TopPanel id={id} pageType={pageType} />
          <VideoSummarize />
          <Content />
        </Wrapper>
      </ViewerProvider>
    </React.Fragment>
  );
};

export default ViewerModulePage;
